import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./components/ui/Button";
import { AnimatedLogo } from "./components/AnimatedLogo";

function App() {
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showSecondPage, setShowSecondPage] = useState(false);

  const handleTransition = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setShowSecondPage(true);
    }, 800);
  };

  const handleBack = () => {
    setShowSecondPage(false);
    setIsTransitioning(false);
  };

  return (
    <div className="min-h-screen bg-background overflow-hidden">
      <AnimatePresence mode="wait">
        {!showSecondPage ? (
          <motion.div
            key="page1"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.5 }}
            className="min-h-screen flex flex-col items-center justify-center p-6"
          >
            <motion.div
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="mb-16 flex flex-col md:flex-row items-center justify-center gap-8 md:gap-12"
            >
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{
                  opacity: 1,
                  x: 0,
                  y: [0, -10, 0],
                }}
                transition={{
                  opacity: { delay: 0.4, duration: 0.6 },
                  x: { delay: 0.4, duration: 0.6 },
                  y: {
                    delay: 1.4,
                    duration: 3,
                    repeat: Number.POSITIVE_INFINITY,
                    ease: "easeInOut",
                  },
                }}
                className="w-40 md:w-52"
              >
                <img
                  src="/moh-logo.png"
                  alt="Ministry of Health"
                  className="w-full h-auto"
                />
              </motion.div>

              <motion.div
                initial={{ scaleY: 0 }}
                animate={{ scaleY: 1 }}
                transition={{ delay: 0.5, duration: 0.4 }}
                className="w-px h-24 md:h-32 bg-border hidden md:block"
              />

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{
                  opacity: 1,
                  x: 0,
                  y: [0, -10, 0],
                }}
                transition={{
                  opacity: { delay: 0.3, duration: 0.6 },
                  x: { delay: 0.3, duration: 0.6 },
                  y: {
                    delay: 1.2,
                    duration: 3,
                    repeat: Number.POSITIVE_INFINITY,
                    ease: "easeInOut",
                  },
                }}
                className="w-56 md:w-80"
              >
                <AnimatedLogo />
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="text-center max-w-3xl mb-12"
            >
              <h1
                className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground text-balance leading-relaxed"
                dir="rtl"
              >
                نحو بناء منظومة صحية غير ربحية أكثر استدامة
              </h1>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.8, duration: 0.5 }}
              whileHover={{
                scale: 1.05,
                y: -4,
              }}
              whileTap={{ scale: 0.98 }}
            >
              <Button
                size="lg"
                onClick={handleTransition}
                disabled={isTransitioning}
                className="group relative overflow-hidden px-16 py-7 text-xl rounded-full shadow-lg hover:shadow-2xl hover:shadow-[#2d8659]/30 transition-all duration-500 ease-out bg-gradient-to-r from-[#8fc651] to-[#049ea3]"
              >
                <motion.span
                  className="relative z-10 flex items-center gap-3 text-white font-semibold"
                  dir="rtl"
                >
                  انطلاق المشروع
                </motion.span>
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0"
                  initial={{ x: "-100%" }}
                  whileHover={{ x: "100%" }}
                  transition={{
                    duration: 0.6,
                    ease: "easeInOut",
                  }}
                />
                <motion.div
                  className="absolute inset-0 bg-white/10"
                  initial={{ opacity: 0 }}
                  whileHover={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                />
              </Button>
            </motion.div>
          </motion.div>
        ) : (
          <motion.div
            key="page2"
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="min-h-screen flex items-center justify-center p-6 md:p-12"
          >
            <div className="w-full max-w-6xl mx-auto">
              <div className="flex flex-col md:flex-row items-center justify-center gap-12 md:gap-20">
                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3, duration: 0.6 }}
                  className="flex justify-center"
                >
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.3 }}
                    className="w-72 h-72 md:w-96 md:h-96 rounded-3xl flex items-center justify-center"
                  >
                    <img
                      src="/qr-code.png"
                      alt="QR Code"
                      className="w-full h-full object-contain"
                    />
                  </motion.div>
                </motion.div>

                <div className="flex flex-col items-center md:items-start space-y-8">
                  <motion.div
                    initial={{ opacity: 0, x: 50 }}
                    animate={{
                      opacity: 1,
                      x: 0,
                      y: [0, -10, 0],
                    }}
                    transition={{
                      opacity: { delay: 0.3, duration: 0.6 },
                      x: { delay: 0.3, duration: 0.6 },
                      y: {
                        delay: 1.2,
                        duration: 3,
                        repeat: Number.POSITIVE_INFINITY,
                        ease: "easeInOut",
                      },
                    }}
                    className="w-80 md:w-[28rem]"
                  >
                    <AnimatedLogo />
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6, duration: 0.6 }}
                    className="text-center md:text-right space-y-4"
                    dir="rtl"
                  >
                    <h2 className="text-lg md:text-xl lg:text-3xl font-bold text-[#2d8659] leading-relaxed text-pretty">
                      ندعوكم للتسجيل في برنامج نسيج
                    </h2>
                    <p className="text-lg md:text-xl lg:text-3xl font-bold text-[#2d8659] leading-relaxed text-pretty">
                      للمساهمة في تعزيز كفاءة واستدامة
                    </p>
                    <p className="text-lg md:text-xl lg:text-3xl font-bold text-[#2d8659] leading-relaxed text-pretty">
                      .القطاع الصحي غير الربحي
                    </p>
                  </motion.div>
                </div>
              </div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8, duration: 0.5 }}
                className="mt-16 flex justify-center"
              >
                <Button
                  variant="outline"
                  size="lg"
                  onClick={handleBack}
                  className="px-10 py-6 text-lg rounded-full hover:bg-accent/10 transition-all duration-500 ease-out bg-transparent"
                  dir="rtl"
                >
                  رجوع للخلف
                </Button>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 0.03, scale: 1 }}
              transition={{ delay: 0.5, duration: 1.5 }}
              className="absolute top-10 right-10 w-72 h-72 rounded-full bg-[#2d8659] blur-3xl"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 0.03, scale: 1 }}
              transition={{ delay: 0.7, duration: 1.5 }}
              className="absolute bottom-10 left-10 w-96 h-96 rounded-full bg-[#4a9d7f] blur-3xl"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;
